import React, { useState, useEffect } from 'react'
import Dialogger from './Dialogger'
import { Link } from 'gatsby'
import { Alert } from 'react-bootstrap'
import { setSelectedProject } from '../utils/selectedProject'

const TOKENS_URL = '/content/tokens.json'
const HEALTH_URL = '/content/health.json'
const NATURA_URL = '/content/natura.json'

const MIME_BY_EXT = {
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    gif: 'image/gif',
    webp: 'image/webp',
    svg: 'image/svg+xml',
    pdf: 'application/pdf',
}

const sha256Hex = async (text) => {
    const bytes = new TextEncoder().encode(text)
    const digest = await crypto.subtle.digest('SHA-256', bytes)
    return Array.from(new Uint8Array(digest))
        .map(b => b.toString(16).padStart(2, '0'))
        .join('')
}

const sha256Bytes = async (text) => {
    const bytes = new TextEncoder().encode(text)
    return crypto.subtle.digest('SHA-256', bytes)
}

const base64ToBytes = (base64) => {
    const binary = atob(base64)
    const bytes = new Uint8Array(binary.length)
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
    return bytes
}

const bytesToBase64 = (bytes) => {
    let binary = ''
    for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i])
    return btoa(binary)
}

// Desembrulha a CONTENT_KEY publicada pra este token: a mesma chave de embrulho
// derivada do texto do token (gerada em scripts/generate-protected-media.js).
const unwrapContentKey = async (tokenEntry, token) => {
    const wrapKeyBytes = await sha256Bytes('wrapkey:' + token)
    const wrapKey = await crypto.subtle.importKey('raw', wrapKeyBytes, 'AES-GCM', false, ['decrypt'])
    const iv = base64ToBytes(tokenEntry.wrapIv)
    const payload = base64ToBytes(tokenEntry.wrappedKey)
    const rawContentKey = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, wrapKey, payload)
    return crypto.subtle.importKey('raw', rawContentKey, 'AES-GCM', false, ['decrypt'])
}

const guessMime = (url) => {
    const withoutEnc = url.replace(/\.enc$/, '')
    const ext = withoutEnc.split('.').pop().toLowerCase()
    return MIME_BY_EXT[ext] || 'application/octet-stream'
}

// Baixa um arquivo criptografado (.enc), decripta com a CONTENT_KEY e devolve uma
// data: URI — funciona em qualquer aba/contexto, ao contrário de um blob: object URL.
const decryptMediaUrl = async (contentKey, url) => {
    if (!url || typeof url !== 'string' || !url.endsWith('.enc')) return url

    const res = await fetch(url)
    const buffer = await res.arrayBuffer()
    const iv = buffer.slice(0, 12)
    const payload = buffer.slice(12)
    const plaintext = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, contentKey, payload)
    const base64 = bytesToBase64(new Uint8Array(plaintext))
    return `data:${guessMime(url)};base64,${base64}`
}

const decryptProjectItem = async (contentKey, item) => {
    const decrypted = { ...item }
    if (decrypted.imageUrl) decrypted.imageUrl = await decryptMediaUrl(contentKey, decrypted.imageUrl)
    if (decrypted.docUrl) decrypted.docUrl = await decryptMediaUrl(contentKey, decrypted.docUrl)
    if (Array.isArray(decrypted.otherImages)) {
        decrypted.otherImages = await Promise.all(decrypted.otherImages.map(url => decryptMediaUrl(contentKey, url)))
    }
    if (Array.isArray(decrypted.pdfs)) {
        decrypted.pdfs = await Promise.all(decrypted.pdfs.map(url => decryptMediaUrl(contentKey, url)))
    }
    if (Array.isArray(decrypted.projects)) {
        decrypted.projects = await Promise.all(decrypted.projects.map(sub => decryptProjectItem(contentKey, sub)))
    }
    return decrypted
}

const ToList = ({ data, title }) => (
    <>
        <h2>{title}</h2>
        <div className="row mt-2">
            <hr />
            {
                data.length && data.map((el, i) => (
                    <div className="col-lg-4 col-md-6 col-sm-12 d-flex justify-content-center" key={i}>
                        <Link to={`/detalhes`}
                            onClick={() => setSelectedProject(el)}
                            className="link primary"
                        >
                            <img src={el.imageUrl} className="img-fluid" alt={el.title} />
                        </Link>
                    </div>
                ))
            }
            <hr />
        </div>
    </>
)

const Health = () => {
    const [health, setHealth] = useState(false)
    const [natura, setNatura] = useState(false)
    const [error, setError] = useState(false)
    const [token, setToken] = useState(false)
    const [isAuthed, setIsAuthed] = useState(false)
    const [loaded, setLoaded] = useState(false)
    const [show, setShow] = useState(false)

    const handleClose = () => setShow(false)
    const handleShow = () => setShow(true)

    useEffect(() => {
        const getInitials = async () => {
            setLoaded(false)
            const savedToken = window.localStorage.getItem('TOKEN')

            if (!savedToken) {
                return setLoaded(true)
            }

            try {
                await authorize(savedToken)
            } catch (error) {
                setError('Ocorreu um erro, verifique suas credenciais')
            } finally {
                setLoaded(true)
            }
        }
        getInitials()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const findValidToken = async (hashedToken) => {
        const res = await fetch(TOKENS_URL)
        const { items } = await res.json()
        return (items || []).find(item => item.hashedToken === hashedToken)
    }

    const authorize = async (rawToken) => {
        try {
            const hashedToken = await sha256Hex('lookup:' + rawToken)
            const found = await findValidToken(hashedToken)

            if (!found) {
                window.localStorage.removeItem('TOKEN')
                setShow(false)
                return setError('Token não reconhecida')
            }

            if (new Date(found.expDate).getTime() <= Date.now()) {
                window.localStorage.removeItem('TOKEN')
                setIsAuthed(false)
                setShow(false)
                return setError('Sua token encontra-se expirada. Por favor, insira uma nova')
            }

            const contentKey = await unwrapContentKey(found, rawToken)
            await getHealthInfo(contentKey)
            await getNaturaInfo(contentKey)

            setIsAuthed(true)
            window.localStorage.setItem('TOKEN', rawToken)

        } catch (error) {
            return setError('Ocorreu um erro...')
        } finally {
            setLoaded(true)
            setShow(false)
        }
    }

    const handleValidate = async () => {
        setLoaded(false)
        try {
            if (!token) {
                setError('Você precisa inserir um token')
                return
            }
            await authorize(token)

        } catch (error) {
            return setError('Ocorreu um erro...')

        } finally {
            setLoaded(true)
            setShow(false)
        }
    }

    const updateTokenField = (e) => {
        setToken(e.target.value)
    }

    const getHealthInfo = async (contentKey) => {
        const res = await fetch(HEALTH_URL)
        const { items } = await res.json()
        setHealth(await Promise.all(items.map(item => decryptProjectItem(contentKey, item))))
    }

    const getNaturaInfo = async (contentKey) => {
        const res = await fetch(NATURA_URL)
        const { items } = await res.json()
        setNatura(await Promise.all(items.map(item => decryptProjectItem(contentKey, item))))
    }

    return (
        <>
            {
                !loaded && (
                    <p>Carregando...</p>
                )
            }
            {
                !isAuthed && (
                    <>
                        {error && (
                            <Alert className="mt-2" variant="warning" onClose={() => setError(false)} dismissible>
                                <Alert.Heading>
                                    <b>Oops...</b>
                                </Alert.Heading>
                                <span>{error}</span>
                            </Alert>
                        )}
                        <Dialogger loaded={loaded} updateTokenField={updateTokenField} show={show} handleClose={handleClose} handleValidate={handleValidate} handleShow={handleShow} />
                    </>
                )
            }
            {
                isAuthed && (
                    <>
                        {!health ? (
                            <p>Não há projetos a serem exibidos</p>

                        ) : (
                            <ToList data={health} title="Health" />
                        )}

                        {!natura ? (
                            <p>Não há projetos a serem exibidos</p>

                        ) : (
                            <ToList data={natura} title="Natura" />
                        )}

                    </>
                )
            }
        </>
    )
}

export default Health
